import React, { useEffect, useRef, useState } from 'react';
import { PrioritizeItem } from './PrioritizeDetailModal';
import '../styles/ReleasesPage.css';

/* Markers / icons (shared look with the Releases picker) */
const SquareMarker: React.FC<{ color?: string }> = ({ color = 'var(--brand-500)' }) => (
  <span className="ini-sq" style={{ background: color }} />
);
const ProductIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/></svg>
);
const ComponentIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
);

export type FeatureLinkField = 'releaseParentId' | 'objectiveParentId' | 'initiativeParentId';

/* Add-feature tree picker (product → component → feature). Links the picked
   feature to a parent record (release / objective / initiative) via the given
   link field. Reused across the Releases, Objectives and Initiatives lists. */
const AddLinkedFeaturePopup: React.FC<{
  items: PrioritizeItem[];
  parentId: string;
  linkField: FeatureLinkField;
  onToggle: (featureId: string, checked: boolean) => void;
  onClose: () => void;
}> = ({ items, parentId, linkField, onToggle, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [openNodes, setOpenNodes] = useState<Set<string>>(() => {
    const s = new Set<string>();
    items.forEach(i => { if (i.kind === 'product' || i.kind === 'epic') s.add(i.id); });
    return s;
  });
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const toggleNode = (id: string) => setOpenNodes(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const allFeatures = items.filter(i => i.kind === 'feature');
  const products = items.filter(i => i.kind === 'product');
  const epicsOf = (productId: string) => items.filter(i => i.kind === 'epic' && i.parentId === productId);
  const featuresOf = (pid: string, isProduct: boolean) =>
    items.filter(i => i.kind === 'feature' && (isProduct ? i.productParentId === pid : i.parentId === pid));
  // Features that don't sit under a product/component in the tree — shown flat
  // at the root so nothing is ever hidden from the picker.
  const treeFeatureIds = new Set<string>();
  products.forEach(p => {
    featuresOf(p.id, true).forEach(f => treeFeatureIds.add(f.id));
    epicsOf(p.id).forEach(ep => featuresOf(ep.id, false).forEach(f => treeFeatureIds.add(f.id)));
  });
  const looseFeatures = allFeatures.filter(f => !treeFeatureIds.has(f.id));

  const q = query.trim().toLowerCase();
  const matches = (f: PrioritizeItem) => !q || f.name.toLowerCase().includes(q);

  const FeatureRow: React.FC<{ f: PrioritizeItem; depth: number }> = ({ f, depth }) => {
    const checked = f[linkField] === parentId;
    if (!matches(f)) return null;
    return (
      <button type="button" className="rel-pick-feature" style={{ paddingLeft: 12 + depth * 22 }} onClick={() => onToggle(f.id, !checked)}>
        <SquareMarker color={f.color || '#94a3b8'} />
        <span className="rel-pick-feature-name">{f.name}</span>
        {checked && <svg className="rel-pick-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
      </button>
    );
  };

  return (
    <div className="rel-pick-popup" ref={ref} onClick={e => e.stopPropagation()}>
      <div className="rel-pick-header">
        <span className="rel-pick-title">Add feature</span>
        <button type="button" className="rel-pick-close" onClick={onClose}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
      </div>
      <div className="rel-pick-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        <input autoFocus placeholder="Search features…" value={query} onChange={e => setQuery(e.target.value)} />
      </div>
      <div className="rel-pick-tree">
        {allFeatures.length === 0 && <div className="rel-pick-empty">No features yet — create features on the Features page first.</div>}
        {products.map(p => (
          <React.Fragment key={p.id}>
            <button type="button" className="rel-pick-node" style={{ paddingLeft: 12 }} onClick={() => toggleNode(p.id)}>
              <svg className={`rel-pick-chev ${openNodes.has(p.id) ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              <ProductIcon /><span>{p.name}</span>
            </button>
            {openNodes.has(p.id) && (
              <>
                {/* Feature linked directly to a product → single indent (depth 1). */}
                {featuresOf(p.id, true).map(f => <FeatureRow key={f.id} f={f} depth={1} />)}
                {epicsOf(p.id).map(ep => (
                  <React.Fragment key={ep.id}>
                    <button type="button" className="rel-pick-node" style={{ paddingLeft: 12 + 22 }} onClick={() => toggleNode(ep.id)}>
                      <svg className={`rel-pick-chev ${openNodes.has(ep.id) ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                      <ComponentIcon /><span>{ep.name}</span>
                    </button>
                    {/* Feature linked to a component → double indent (depth 2). */}
                    {openNodes.has(ep.id) && featuresOf(ep.id, false).map(f => <FeatureRow key={f.id} f={f} depth={2} />)}
                  </React.Fragment>
                ))}
              </>
            )}
          </React.Fragment>
        ))}
        {looseFeatures.map(f => <FeatureRow key={f.id} f={f} depth={0} />)}
      </div>
    </div>
  );
};

export default AddLinkedFeaturePopup;
